/**
 * A tabela do Conferir envio (E144.F4, GUS-105 + GUS-135; E165.F1.T05, HTML 6.8): uma linha por CCB
 * — a CCB (com "Documentos de apoio vinculados à CCB" quando há apoio), o exame e o destino —, a
 * linha de um caso por CCB e, recolhido sob "Ver arquivos e identificação", o processo, a pasta, o
 * CPF mascarado e os arquivos recebidos. Os apoios nunca contam como CCB (ADR-0098).
 *
 * O destino diz para onde a CCB vai: "Novo caso" ou o caso que já a tem, aberto ou fechado, que se
 * abre daqui (E162.F2.T04). Embaixo da linha, a escolha explícita para cada CCB que já tem caso
 * (E165.F1.T06, ADR-0114 §2). A custódia não está no Conferir (GUS-202, ADR-0114 §3): o recibo fica
 * no Enviar e no caso.
 *
 * E177.F5 (GUS-253, ADR-0130): a CCB declarada diz, na linha, as contagens ("2 documentos · 1
 * compartilhado") e o identificador divergente; o conflito e os documentos por CCB entram antes da
 * identificação (`porCcb`), que passa a "Ver identificação", sem a lista de arquivos.
 */
import { useState, type ReactNode } from 'react';

import { Badge } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ContratoDoEnvioData, EscolhaDoContrato } from '@/lib/abertura_do_caso';

import { blocosDoEnvio, type BlocoDaCcb } from './blocosDasCcbs';
import { contagensDoBloco, envioPorCcb, resumoDaCcb } from './conferenciaPorCcb';
import type { EscolhasDoEnvio } from './escolhaDaCcb';
import { EscolhasDasCcbs } from './EscolhasDasCcbs';
import { CartaoDaAbertura, Recolhivel } from './PecasDaAbertura';
import {
  casoQueJaTemACcb,
  destinoDoContrato,
  pendenciasDoContrato,
  resumoDosContratos,
  rotuloDoContrato,
  tituloDosContratos,
} from './rotulosDoEnvio';

const t = ptBR.abertura.conferirEnvio;

/** O `th` do molde 6.8: IBM Plex Mono 12px, maiúsculo, com o fundo da superfície. */
const CABECALHO =
  'border-b border-border bg-surface-2 px-[9px] py-3 text-left font-titulo text-[12px] font-medium uppercase tracking-[0.06em] text-fg-muted';
const CELULA = 'border-b border-border px-[9px] py-[15px] align-top text-[14px]';

/** A linha da CCB: as contagens da declarada, ou o aviso dos apoios no envio sem CCB declarada. */
function MaterialDaLinha({
  contrato,
  blocos,
}: {
  contrato: ContratoDoEnvioData;
  blocos: readonly BlocoDaCcb[];
}): JSX.Element | null {
  const bloco = blocos.find((b) => b.chave === contrato.ccb?.id);
  if (bloco !== undefined) {
    return (
      <small
        className="mt-2 block text-[13px] text-fg-muted"
        data-testid={`conferir-contagens-${contrato.chave}`}
      >
        {resumoDaCcb(contrato, contagensDoBloco(blocos, bloco))}
      </small>
    );
  }
  return contrato.apoios.length > 0 ? (
    <small className="mt-2 block text-[13px] text-fg-muted">{t.apoiosVinculados}</small>
  ) : null;
}

function LinhaDoContrato({
  contrato,
  blocos,
  onAbrirCaso,
}: {
  contrato: ContratoDoEnvioData;
  blocos: readonly BlocoDaCcb[];
  onAbrirCaso: (caseId: string) => void;
}): JSX.Element {
  const caso = casoQueJaTemACcb(contrato);
  return (
    <tr className="[&:last-child>td]:border-b-0" data-testid={`conferir-ccb-${contrato.chave}`}>
      <td className={`${CELULA} text-fg`}>
        <b className="font-semibold">{rotuloDoContrato(contrato)}</b>
        <MaterialDaLinha contrato={contrato} blocos={blocos} />
        {pendenciasDoContrato(contrato).map((p) => (
          <small
            key={p}
            className="mt-1 block text-[13px] text-fg-muted"
            data-testid="conferir-pendencia"
          >
            {p}
          </small>
        ))}
      </td>
      <td className={`${CELULA} text-fg`}>{ptBR.abertura.servico.eletronica.nome}</td>
      <td className={`${CELULA} text-fg`} data-testid={`conferir-destino-${contrato.chave}`}>
        {caso === null ? (
          destinoDoContrato(contrato)
        ) : (
          <button
            type="button"
            data-testid={`conferir-abrir-caso-${caso.case_id}`}
            onClick={() => onAbrirCaso(caso.case_id)}
            className="text-left text-accent-700 underline underline-offset-2"
          >
            {destinoDoContrato(contrato)}
          </button>
        )}
      </td>
    </tr>
  );
}

function ArquivosEIdentificacao(props: {
  /** E177.F5: vazio no envio por CCB — os arquivos estão nos grupos. */
  arquivos: readonly string[];
  titulo: string;
  pasta: string | null;
  processo: string | null;
  cpf: string | null;
}): JSX.Element {
  const [aberto, setAberto] = useState(false);
  const dados: readonly [string, string | null][] = [
    [t.rotuloProcesso, props.processo],
    [t.rotuloPasta, props.pasta],
    [t.rotuloCpf, props.cpf],
  ];
  return (
    <div className="mt-[14px]">
      <Recolhivel
        titulo={props.titulo}
        aberto={aberto}
        onAberto={setAberto}
        testId="conferir-arquivos"
      >
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="conferir-identificacao">
          {dados.map(([rotulo, valor]) => (
            <div key={rotulo}>
              <dt className="font-titulo text-[12px] uppercase tracking-[0.06em] text-fg-muted">
                {rotulo}
              </dt>
              <dd className="mt-1 font-mono text-[14px] text-fg">{valor ?? t.naoInformado}</dd>
            </div>
          ))}
        </dl>
        {props.arquivos.length > 0 ? (
          <ul data-testid="conferir-lista-de-arquivos">
            {props.arquivos.map((nome) => (
              <li
                key={nome}
                className="flex items-center justify-between gap-[15px] border-b border-border py-3 text-[14px] last:border-b-0"
              >
                <span className="min-w-0 truncate text-fg">{nome}</span>
                <Badge variant="neutral">{t.recebido}</Badge>
              </li>
            ))}
          </ul>
        ) : null}
      </Recolhivel>
    </div>
  );
}

export interface ContratosDoEnvioProps {
  contratos: readonly ContratoDoEnvioData[];
  /** Os arquivos recebidos no envio (os originais do recibo), na ordem do recibo. */
  arquivos: readonly string[];
  pasta: string | null;
  processo: string | null;
  /** E165.F1.T05: o CPF informado, mascarado pelo servidor. */
  cpf: string | null;
  onAbrirCaso: (caseId: string) => void;
  /** E165.F1.T06: a escolha de cada CCB que já tem caso, pela chave. */
  escolhas: EscolhasDoEnvio;
  onEscolher: (chave: string, escolha: EscolhaDoContrato) => void;
  disabled?: boolean;
  /** E177.F5: no envio por CCB, o conflito e os documentos por CCB, antes da identificação. */
  porCcb?: ReactNode;
}

export function ContratosDoEnvio(props: ContratosDoEnvioProps): JSX.Element {
  const { contratos } = props;
  // Sem contrato nenhum o Conferir avisa à parte (E133.F2.C01); "segue como um caso" seria falso.
  const semContrato = contratos.length > 0 && contratos.every((c) => !c.identificado);
  const envio = { contratos: [...contratos], perguntas: [], em_verificacao: false };
  const blocos = blocosDoEnvio(envio, []);
  const porCcb = envioPorCcb(envio);
  return (
    <CartaoDaAbertura titulo={tituloDosContratos(contratos)} testId="conferir-ccbs">
      {semContrato ? <p className="mb-3 text-[14px] text-fg-muted">{t.semContratoTexto}</p> : null}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse">
          <thead>
            <tr>
              <th className={CABECALHO}>{t.colunaCcb}</th>
              <th className={CABECALHO}>{t.colunaExame}</th>
              <th className={CABECALHO}>{t.colunaDestino}</th>
            </tr>
          </thead>
          <tbody>
            {contratos.map((c) => (
              <LinhaDoContrato
                key={c.chave}
                contrato={c}
                blocos={blocos}
                onAbrirCaso={props.onAbrirCaso}
              />
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[14px] text-fg-muted" data-testid="conferir-resumo">
        {resumoDosContratos(contratos, props.escolhas)}
      </p>
      <EscolhasDasCcbs
        contratos={contratos}
        escolhas={props.escolhas}
        onEscolher={props.onEscolher}
        onAbrirCaso={props.onAbrirCaso}
        disabled={props.disabled ?? false}
      />
      {porCcb ? props.porCcb : null}
      <ArquivosEIdentificacao
        arquivos={porCcb ? [] : props.arquivos}
        titulo={porCcb ? ptBR.abertura.conferirPorCcb.verIdentificacao : t.verArquivos}
        pasta={props.pasta}
        processo={props.processo}
        cpf={props.cpf}
      />
    </CartaoDaAbertura>
  );
}
